import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { useLazyGetFactoryPrintDeliveryPreviewQuery } from '@/store/api/factoryApi'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: form_frmFactoryDeliverySubForm.txt → btnPrintRp_Click
// (`DoCmd.OpenReport "rpFactoryGdn", acViewReport`). Same pattern as
// ReceivePrintDialog: BE trả HTML (view_factory_rp_gdn_detail by gdn_id), FE
// preview trong iframe, button Print → printAndUploadReport (PDF + archive +
// browser print dialog).

interface DeliveryPrintDialogProps {
  open: boolean
  factoryCode: string
  /** Delivery PK `id` (serial). Print payload tra theo id (Access deliveryId). */
  deliveryId: number | null
  /** GDN ID — chỉ để hiển thị tiêu đề + tên file PDF. */
  gdnId?: string | null
  canEdit?: boolean
  onClose: () => void
}

export default function DeliveryPrintDialog({
  open,
  factoryCode,
  deliveryId,
  gdnId,
  canEdit = true,
  onClose,
}: DeliveryPrintDialogProps) {
  const [previewing, setPreviewing] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [triggerPreview] = useLazyGetFactoryPrintDeliveryPreviewQuery()

  const previewTitle = gdnId
    ? `Print Preview - Delivery (${gdnId})`
    : 'Print Preview - Delivery'
  const iframeTitle = 'Delivery Print Preview'

  useEffect(() => {
    if (!open || deliveryId == null || previewing || previewHtml) return
    let cancelled = false
    const run = async () => {
      setPreviewing(true)
      try {
        const result = await triggerPreview({ factoryCode, deliveryId }).unwrap()
        if (cancelled) return
        // GDN = report có bảng ATTACHED STACK co giãn → BE trả nhiều tờ
        // `.gdn-page` (bảng nhiều dòng tự break trang). `pageSelector` cho mỗi
        // .gdn-page thành 1 tờ A4 riêng (preview tách trang = PDF).
        setPreviewHtml(buildPrintPreviewHtml(result.html, {
          styleId: 'delivery-print-preview-overrides',
          pageSelector: '.gdn-page',
          pagePadding: '9mm 6mm',
          extraCss: '.ctl img { object-position: left center !important; } .gdn-page + .gdn-page { break-before: page; page-break-before: always; }',
        }))
        setToastMessage(result.message ?? 'Print preview generated.')
      } catch (err) {
        if (cancelled) return
        const e = err as { status?: number; data?: { detail?: string } }
        if (e?.status === 422) {
          setToastMessage(e.data?.detail || 'Cannot print this record')
        } else if (e?.status === 404) {
          setToastMessage('Delivery not found')
        } else {
          setToastMessage('Failed to generate print preview.')
        }
        onClose()
      } finally {
        if (!cancelled) setPreviewing(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deliveryId, factoryCode])

  const handleClose = () => {
    setPreviewHtml('')
    onClose()
  }

  return (
    <>
      {previewing && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog open={Boolean(previewHtml)} onClose={handleClose} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={previewTitle}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Delivery</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Delivery</Box>
            {gdnId ? ` (${gdnId})` : ''}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} size="small">
              Close
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  const printTitle = `${sanitizePdfFileNamePart(gdnId ?? 'delivery')}.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpFactoryGdn',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
